/**
 * Demo board — the founder's son's actual device, tile-for-tile.
 *
 * A fixed 6-row × 8-column board (48 tiles), no folders, everything on one
 * screen — exactly like the plastic board he uses at home, but with the
 * wasted device-control row (volume, voice-gender, REC buttons) replaced by
 * what matters: the family's own photos and favorites.
 *
 * Layout (locked by the founder 2026-07-12):
 *   row 1  feelings   — I feel, happy, sad, angry, tired, sick, hungry, thirsty
 *   row 2  needs      — I need, bathroom, brush teeth, shower, get dressed, sleep, help, +
 *   row 3  wants      — I want, go outside, go home, play, music, TV, toys, be alone
 *   row 4  favorites  — more + seven "your picture here" favorite tiles
 *   row 5  people     — two more favorites + Mom, Dad, brother, sister, friend, stop
 *   row 6  social     — hello, yes, no, please, thank you, I love you, I don't know, bye
 *
 * Art: original SonSpeak animated pictograms (anim), reviewed shipped assets
 * (img), emoji (emoji), and "your picture here" photo frames (photo). The
 * personalization tiles are grouped in rows 4–5 so blanks never scatter.
 */

export const BOARD_COLS = 8;
export const BOARD_ROWS = 6;

// Category color per row-band (the top strip on each tile — meaning, not decoration).
const C = {
  feel: "var(--brand-purple, #C39BD3)",
  need: "var(--sage, #7FB685)",
  want: "var(--brand-green, #6BCB77)",
  fav: "var(--brand-red, #FF6B6B)",
  people: "var(--brand-blue, #4D96FF)",
  core: "var(--gold, #FFB627)",
};

// Parent-facing categories + their default colors (hex, for the color panel).
// A caregiver can recolor any of these; every tile in that category follows.
export const CATEGORIES = [
  { key: "feel", label: "Feelings", color: "#C39BD3" },
  { key: "need", label: "Needs", color: "#7FB685" },
  { key: "want", label: "Wants", color: "#6BCB77" },
  { key: "fav", label: "Favorites", color: "#FF6B6B" },
  { key: "people", label: "People", color: "#4D96FF" },
  { key: "core", label: "Core words", color: "#FFB627" },
];
export const DEFAULT_BOARD_BG = "#F6EEDD"; // cream
// The default palette (category colors + board background). Colors are a
// PARENT setting — the child board never shows a color control.
export const DEFAULT_COLORS = {
  feel: "#C39BD3",
  need: "#7FB685",
  want: "#6BCB77",
  fav: "#FF6B6B",
  people: "#4D96FF",
  core: "#FFB627",
  bg: DEFAULT_BOARD_BG,
};
const CAT_BY_STRIP = {
  [C.feel]: "feel",
  [C.need]: "need",
  [C.want]: "want",
  [C.fav]: "fav",
  [C.people]: "people",
  [C.core]: "core",
};

const photo = (label, speak) => ({
  id: `ph-${label.toLowerCase().replace(/[^a-z]+/g, "-")}`,
  label,
  speak: speak || label,
  art: { type: "photo" },
  strip: C.fav,
});

// Living tiles — a sprite-sheet flipbook that plays ONCE on tap, inside the
// tile, then settles back to its poster frame. Motion never leaves the tile
// and never delays speech.
//
// The sample sprites are AI-generated placeholder imagery (faces intentionally
// blurred): a concept preview of the "living board". In the real app these
// become the FAMILY'S OWN photo + recorded voice, added by a guardian with
// consent — never shipped stock. cols/rows describe the sprite grid; poster
// and seq are 0-based cell indices (row-major).
const SPRITES = {
  family: "/demo/sprites/family-actions-4x2.webp", // top row = hello, bottom row = Mom
  music: "/demo/sprites/music-dance-4x2.webp",
  pet: "/demo/sprites/my-pet-cats-4x2.webp",
};
const clip = (id, label, speak, sprite, poster, seq, strip) => ({
  id,
  label,
  speak: speak || label,
  art: { type: "clip", sprite, cols: 4, rows: 2, poster, seq },
  strip,
  living: true,
});

/** The board as six explicit rows of eight tiles. */
export const FIXED_BOARD_ROWS = [
  // row 1 — feelings
  [
    { id: "ifeel",   label: "I feel",   speak: "I feel",         art: { type: "emoji", char: "💛" }, strip: C.feel },
    { id: "happy",   label: "happy",    speak: "I feel happy",   art: { type: "img", src: "/demo/noto-happy.svg" }, strip: C.feel },
    { id: "sad",     label: "sad",      speak: "I feel sad",     art: { type: "img", src: "/demo/noto-sad.svg" }, strip: C.feel },
    { id: "angry",   label: "angry",    speak: "I feel angry",   art: { type: "img", src: "/demo/noto-angry.svg" }, strip: C.feel },
    { id: "tired",   label: "tired",    speak: "I feel tired",   art: { type: "img", src: "/demo/noto-tired.svg" }, strip: C.feel },
    { id: "sick",    label: "sick",     speak: "I feel sick",    art: { type: "emoji", char: "🤒" }, strip: C.feel },
    { id: "hungry",  label: "hungry",   speak: "I am hungry",    art: { type: "emoji", char: "😋" }, strip: C.feel },
    { id: "thirsty", label: "thirsty",  speak: "I am thirsty",   art: { type: "emoji", char: "🥤" }, strip: C.feel },
  ],
  // row 2 — needs
  [
    { id: "ineed",   label: "I need",       speak: "I need",             art: { type: "emoji", char: "🤲" }, strip: C.need },
    { id: "bathroom", label: "bathroom",    speak: "I need the bathroom", art: { type: "emoji", char: "🚽" }, strip: C.need },
    { id: "teeth",   label: "brush teeth",  speak: "brush my teeth",     art: { type: "emoji", char: "🪥" }, strip: C.need },
    { id: "shower",  label: "shower",       speak: "I want a shower",    art: { type: "emoji", char: "🚿" }, strip: C.need },
    { id: "dressed", label: "get dressed",  speak: "help me get dressed", art: { type: "emoji", char: "👕" }, strip: C.need },
    { id: "sleep",   label: "sleep",        speak: "I want to sleep",    art: { type: "img", src: "/demo/noto-sleep.svg" }, strip: C.need },
    { id: "help",    label: "help",         speak: "help",               art: { type: "anim", name: "help" }, strip: C.need },
    photo("my medicine", "I need my medicine"),
  ],
  // row 3 — wants
  [
    { id: "iwant",   label: "I want",     speak: "I want",             art: { type: "anim", name: "want" }, strip: C.want },
    { id: "outside", label: "go outside", speak: "I want to go outside", art: { type: "emoji", char: "🌳" }, strip: C.want },
    { id: "gohome",  label: "go home",    speak: "I want to go home",  art: { type: "emoji", char: "🏠" }, strip: C.want },
    { id: "play",    label: "play",       speak: "I want to play",     art: { type: "img", src: "/demo/noto-play.svg" }, strip: C.want },
    clip("music", "music", "I want music", SPRITES.music, 0, [0, 1, 2, 3, 4, 5, 6, 7, 6, 5, 4, 3, 2, 1, 0], C.want),
    { id: "tv",      label: "TV",         speak: "I want to watch TV", art: { type: "emoji", char: "📺" }, strip: C.want },
    { id: "toys",    label: "toys",       speak: "I want my toys",     art: { type: "emoji", char: "🧸" }, strip: C.want },
    { id: "alone",   label: "be alone",   speak: "I want to be alone", art: { type: "emoji", char: "🧘" }, strip: C.want },
  ],
  // row 4 — "more" + the family's favorites (your photos go here)
  [
    { id: "more",    label: "more",           speak: "more",            art: { type: "anim", name: "more" }, strip: C.core },
    photo("favorite food", "my favorite food"),
    photo("favorite snack", "my favorite snack"),
    photo("favorite drink", "my favorite drink"),
    photo("favorite toy", "my favorite toy"),
    photo("favorite show", "my favorite show"),
    photo("favorite song", "my favorite song"),
    photo("our park", "I want to go to the park"),
  ],
  // row 5 — two more favorites + the people
  [
    clip("mypet", "my pet", "my pet", SPRITES.pet, 0, [0, 1, 2, 3, 4, 5, 6, 7, 6, 5, 4, 3, 2, 1, 0], C.fav),
    photo("our dinner", "what's for dinner"),
    clip("mom", "Mom", "Mom", SPRITES.family, 4, [4, 5, 6, 7, 6, 5, 4], C.people),
    { id: "dad",     label: "Dad",     speak: "Dad",     art: { type: "img", src: "/demo/noto-dad.svg" }, strip: C.people },
    { id: "brother", label: "brother", speak: "brother", art: { type: "img", src: "/demo/noto-brother.svg" }, strip: C.people },
    { id: "sister",  label: "sister",  speak: "sister",  art: { type: "emoji", char: "👧" }, strip: C.people },
    { id: "friend",  label: "friend",  speak: "friend",  art: { type: "img", src: "/demo/noto-friend.svg" }, strip: C.people },
    { id: "stop",    label: "stop",    speak: "stop",    art: { type: "anim", name: "stop" }, strip: C.core },
  ],
  // row 6 — social
  [
    clip("hi", "hello", "hello", SPRITES.family, 0, [0, 1, 2, 1, 0], C.core),
    { id: "yes",     label: "yes",         speak: "yes",         art: { type: "anim", name: "yes" }, strip: C.core },
    { id: "no",      label: "no",          speak: "no",          art: { type: "anim", name: "no" }, strip: C.core },
    { id: "please",  label: "please",      speak: "please",      art: { type: "emoji", char: "🙏" }, strip: C.core },
    { id: "thanks",  label: "thank you",   speak: "thank you",   art: { type: "emoji", char: "🤝" }, strip: C.core },
    { id: "iloveu",  label: "I love you",  speak: "I love you",  art: { type: "anim", name: "hug" }, strip: C.people },
    { id: "dontknow", label: "I don't know", speak: "I don't know", art: { type: "emoji", char: "🤷" }, strip: C.core },
    { id: "bye",     label: "bye",         speak: "bye bye",     art: { type: "anim", name: "bye" }, strip: C.core },
  ],
];

/** Flat list, for anything that wants every tile. */
export const FIXED_BOARD_TILES = FIXED_BOARD_ROWS.flat();

// Sample photos for the full-photo "living board" showcase — index-aligned
// with the flat tile order above. AI-generated placeholder imagery (blurred
// faces); in the app every tile is the family's OWN photo. See attribution.json.
const LIVING_PHOTOS = [
  "/demo/living/00-i-feel.webp", "/demo/living/01-happy.webp", "/demo/living/02-sad.webp",
  "/demo/living/03-angry.webp", "/demo/living/04-tired.webp", "/demo/living/05-sick.webp",
  "/demo/living/06-hungry.webp", "/demo/living/07-thirsty.webp", "/demo/living/08-i-need.webp",
  "/demo/living/09-bathroom.webp", "/demo/living/10-brush-teeth.webp", "/demo/living/11-shower.webp",
  "/demo/living/12-get-dressed.webp", "/demo/living/13-sleep.webp", "/demo/living/14-help.webp",
  "/demo/living/15-my-medicine.webp", "/demo/living/16-i-want.webp", "/demo/living/17-go-outside.webp",
  "/demo/living/18-go-home.webp", "/demo/living/19-play.webp", "/demo/living/20-music.webp",
  "/demo/living/21-tv.webp", "/demo/living/22-toy.webp", "/demo/living/23-be-alone.webp",
  "/demo/living/24-more.webp", "/demo/living/25-favorite-food.webp", "/demo/living/26-favorite-snack.webp",
  "/demo/living/27-favorite-drink.webp", "/demo/living/28-favorite-toy.webp", "/demo/living/29-favorite-show.webp",
  "/demo/living/30-favorite-song.webp", "/demo/living/31-our-park.webp", "/demo/living/32-my-pet.webp",
  "/demo/living/33-our-dinner.webp", "/demo/living/34-mom.webp", "/demo/living/35-dad.webp",
  "/demo/living/36-brother.webp", "/demo/living/37-sister.webp", "/demo/living/38-friend.webp",
  "/demo/living/39-stop.webp", "/demo/living/40-hello.webp", "/demo/living/41-yes.webp",
  "/demo/living/42-no.webp", "/demo/living/43-please.webp", "/demo/living/44-thank-you.webp",
  "/demo/living/45-i-love-you.webp", "/demo/living/46-i-don.webp", "/demo/living/47-bye.webp",
];

/**
 * The full-photo Living Board — the Custom board showcase. Every tile carries a
 * real picture; the four living tiles keep their motion clip. This is what the
 * demo/landing render so parents see the 2026 experience, not clip-art.
 */
export const LIVING_BOARD_ROWS = (() => {
  let i = 0;
  return FIXED_BOARD_ROWS.map((row) =>
    row.map((tile) => {
      const src = LIVING_PHOTOS[i];
      i += 1;
      const cat = CAT_BY_STRIP[tile.strip] || "core";
      if (tile.art.type === "clip") return { ...tile, cat }; // living tiles keep their motion
      return { ...tile, cat, art: { type: "pic", src } };
    })
  );
})();

// The two setup questions still greet visitors on /demo; the built board is
// this real 6×8 layout either way.
export const AGE_BANDS = [
  { id: "A", label: "1–3" },
  { id: "B", label: "4–5" },
  { id: "C", label: "6–8" },
  { id: "D", label: "9–12" },
  { id: "E", label: "13+" },
];

export const WORD_LEVELS = [
  { id: 0, label: "Not yet" },
  { id: 1, label: "A few words" },
  { id: 2, label: "Short phrases" },
  { id: 3, label: "Full sentences" },
];
